<template>
  <section
    class="relative mt-4 rounded-xl border border-boba-grey/50 bg-boba-sand-light shadow-sm px-5 pt-8 pb-5 xl:px-8 xl:pt-8 xl:pb-6"
  >
    <!-- Final price on the brand scoreboard, where the countdown sat while live. -->
    <scoreboard-badge class="absolute right-5 -top-5 xl:right-8 xl:-top-6" wide="xl">
      <template #label>
        <p class="flex items-center gap-x-1.5 xl:justify-start xl:pb-1 text-sm leading-none">
          <i class="fa-solid fa-flag-checkered text-sm xl:text-base" aria-hidden="true" />
          <span>Final price</span>
        </p>
      </template>
      <p
        data-test="clearing-price"
        :aria-label="price.aria"
        :title="price.aria"
        class="flex items-baseline gap-x-2 xl:justify-end leading-none font-14seg tracking-widest text-lg xl:text-2xl"
      >
        <template v-if="price.kind === 'cleared'">
          {{ $format((listing.clearing_price as number) / 1e9)
          }}<span class="font-digi tracking-normal text-[0.55em] pl-1">miles/ea</span>
        </template>
        <template v-else-if="price.kind === 'pending'">PENDING</template>
        <template v-else>NO SALE</template>
      </p>
    </scoreboard-badge>

    <div class="xl:pr-56">
      <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 class="text-xl xl:text-2xl font-semibold tracking-tight text-boba-brown">
          Auction ended
        </h2>
        <button
          data-test="how-it-works"
          type="button"
          class="inline-flex items-center gap-1.5 rounded text-sm font-medium text-boba-grey-dark hover:text-boba-brown focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-boba-brown focus-visible:ring-offset-2 focus-visible:ring-offset-boba-sand-light min-h-[44px] xl:min-h-0"
          @click="howItWorks?.open()"
        >
          <i class="fa-regular fa-circle-question" aria-hidden="true" />
          How it works
        </button>
      </div>
      <p v-if="endedAt" class="mt-1.5 text-sm text-boba-grey-dark">
        Ended <span class="text-boba-brown">{{ endedAt }}</span>
      </p>
    </div>
    <how-it-works-dialog ref="howItWorks" :listing="listing" />

    <p
      v-if="stage === 'finalizing'"
      class="mt-3 inline-flex items-center gap-2 text-sm text-boba-grey-dark"
    >
      <i class="fa-regular fa-hourglass-half" aria-hidden="true" />
      Results are being finalized. Check back soon.
    </p>
    <p v-else-if="stage === 'closed'" class="mt-3 text-sm text-boba-grey-dark">
      This auction closed without a clearing price.
    </p>

    <template v-if="signedIn && listing.isSettled()">
      <p
        v-if="bids.length && summary.wonBids > 0"
        data-test="summary"
        class="mt-4 flex items-start gap-3 rounded-lg bg-boba-green/20 ring-1 ring-inset ring-boba-green/70 px-4 py-3 text-sm xl:text-base text-boba-brown"
      >
        <i class="fa-solid fa-trophy mt-0.5 text-boba-green-dark" aria-hidden="true" />
        <span>
          You won {{ summary.wonBids }} of your {{ summary.totalBids }}
          {{ summary.totalBids === 1 ? "bid" : "bids" }},
          {{ summary.wonUnits }} {{ summary.wonUnits === 1 ? "unit" : "units" }}
          total, for
          <span class="font-semibold">{{ $format(fromBaseUnits(summary.milesPaid)) }} miles</span>.
        </span>
      </p>
      <p
        v-else-if="bids.length"
        data-test="summary"
        class="mt-4 flex items-start gap-3 rounded-lg bg-boba-sand ring-1 ring-inset ring-boba-grey/60 px-4 py-3 text-sm xl:text-base text-boba-grey-dark"
      >
        <i class="fa-regular fa-circle-xmark mt-0.5" aria-hidden="true" />
        <span>Your bids did not clear.</span>
      </p>
      <p v-else data-test="summary" class="mt-3 text-sm text-boba-grey-dark">
        You didn't place any bids in this auction.
      </p>
    </template>

    <template v-if="!signedIn">
      <div class="mt-4 border-t border-boba-grey/50"></div>
      <div class="mt-4">
        <slot name="signin" />
      </div>
    </template>

    <div
      v-if="bids.length"
      class="mt-4 rounded-lg border border-boba-grey/50 bg-white overflow-hidden"
    >
      <div
        class="hidden xl:grid gap-x-4 items-center px-5 py-2 bg-boba-sand-light/70 border-b border-boba-grey/50 text-xs font-semibold uppercase tracking-wide text-boba-grey-dark"
        :class="gridCols"
        aria-hidden="true"
      >
        <span>Max / ea</span>
        <span>Qty</span>
        <span v-if="settled">Won</span>
        <span>Placed</span>
        <span>Status</span>
      </div>

      <ul class="divide-y divide-boba-grey/50">
        <li
          v-for="bid in orderedBids"
          :key="`bid-${bid.id}`"
          data-test="bid-row"
          class="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-0.5 items-center px-4 py-3 xl:gap-x-4 xl:px-5 xl:py-3.5"
          :class="[gridCols, bid.bid_status === 'cancelled' ? 'text-boba-grey-dark' : 'text-boba-brown']"
        >
          <p
            class="text-lg font-semibold leading-tight tabular-nums whitespace-nowrap xl:col-start-1 xl:row-start-1"
          >
            {{ $format(bid.bid_amount / 1e9) }}
            <span class="text-sm font-normal text-boba-grey-dark"
              >miles<span class="xl:hidden">/ea</span></span
            >
          </p>

          <div
            class="row-span-3 xl:row-span-1 xl:row-start-1 justify-self-end xl:justify-self-start"
            :class="settled ? 'xl:col-start-5' : 'xl:col-start-4'"
          >
            <bid-status :listing="listing" :bid="bid" detail-class="hidden" />
          </div>

          <div class="col-start-1 flex flex-wrap items-baseline gap-x-1.5 text-sm xl:contents">
            <p class="tabular-nums whitespace-nowrap xl:col-start-2 xl:row-start-1 xl:text-base">
              <span class="text-boba-grey-dark xl:hidden">Qty</span>
              {{ $format(bid.bid_quantity) }}
            </p>
            <template v-if="settled">
              <span
                v-if="unitsWon(bid) !== null"
                class="text-boba-grey-dark xl:hidden"
                aria-hidden="true"
                >·</span
              >
              <p
                data-test="units-won"
                class="tabular-nums whitespace-nowrap xl:col-start-3 xl:row-start-1 xl:text-base"
                :class="unitsWon(bid) === null ? 'text-boba-grey-dark hidden xl:block' : ''"
              >
                <template v-if="unitsWon(bid) === null">—</template>
                <template v-else>
                  {{ $format(unitsWon(bid) as number) }} of {{ $format(bid.bid_quantity) }}<span class="xl:hidden"> won</span>
                </template>
              </p>
            </template>
          </div>

          <p
            class="col-start-1 text-xs xl:text-sm text-boba-grey-dark whitespace-nowrap xl:row-start-1"
            :class="settled ? 'xl:col-start-4' : 'xl:col-start-3'"
          >
            <span class="xl:hidden">Placed </span
            >{{ DateTime.fromISO(bid.created_at).toFormat("MMM d, h:mm a") }}
          </p>
        </li>
      </ul>
    </div>
  </section>
</template>
<script lang="ts" setup>
import { computed, ref } from "vue";
import { DateTime } from "ts-luxon";
import { Listing, ListingBid, byUnitBidDesc, summarizeBids } from "@/types";
import { fromBaseUnits } from "@/util/miles";
import BidStatus from "@/components/Account/BidStatus.vue";
import HowItWorksDialog from "@/components/Listing/HowItWorksDialog.vue";
import ScoreboardBadge from "@/components/ScoreboardBadge.vue";
import currencyFormat from "@/util/currencyFormat";

const props = withDefaults(
  defineProps<{
    listing: Listing;
    bids: ListingBid[];
    /** False for anonymous visitors: the `signin` slot renders where the bid list would. */
    signedIn?: boolean;
  }>(),
  { signedIn: true },
);

const howItWorks = ref<InstanceType<typeof HowItWorksDialog> | null>(null);

const endedAt = computed(() => {
  const at = props.listing.auctionEndsAt();
  return at ? at.toFormat("MMM d, yyyy 'at' h:mm a") : null;
});

/** What the scoreboard shows: the clearing price, PENDING, or NO SALE. */
const price = computed(() => {
  const value = props.listing.clearing_price;
  if (value) {
    return { kind: "cleared" as const, aria: `Final price ${currencyFormat(value / 1e9)} miles per unit` };
  }
  if (!props.listing.isSettled()) {
    return { kind: "pending" as const, aria: "Final price pending" };
  }
  return { kind: "nosale" as const, aria: "No clearing price; nothing sold" };
});

/** Settlement stage for the body copy; independent of whether a price is known yet. */
const stage = computed(() => {
  if (!props.listing.isSettled()) {
    return "finalizing" as const;
  }
  return price.value.kind === "nosale" ? ("closed" as const) : ("settled" as const);
});

const summary = computed(() =>
  summarizeBids(props.bids, props.listing.clearing_price ?? null),
);

const settled = computed(() => props.listing.isSettled());

const orderedBids = computed(() => [...props.bids].sort(byUnitBidDesc));

const gridCols = computed(() =>
  settled.value
    ? "xl:grid-cols-[minmax(7rem,1.2fr)_5.5rem_6rem_minmax(8rem,1fr)_minmax(9rem,1.4fr)]"
    : "xl:grid-cols-[minmax(7rem,1.2fr)_5.5rem_minmax(8rem,1fr)_minmax(9rem,1.4fr)]",
);

/** Units this bid actually won, or null when the question doesn't apply. */
function unitsWon(bid: ListingBid): number | null {
  switch (bid.bid_status) {
    case "won":
    case "unfunded":
    case "failed":
      return bid.filled_quantity ?? 0;
    case "lost":
      return 0;
    default:
      return null;
  }
}
</script>
